import { Box, Chip, Typography } from '@mui/material'
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import CardWithSpace from '../../../components/Cards/CardWithSpace/index.jsx'
import UseTranslation from '../../../hooks/UseTranslation.jsx'
import TitleWithInfo from '../../../components/Cards/helpers/TitleWithInfo.jsx'

const StateChip = ({ positive, label }) => (
  <Chip
    size="small"
    icon={(
      <FiberManualRecordRoundedIcon
        sx={styles.statusDotIcon(positive ? '#29CC8B' : '#92929d')}
      />
    )}
    label={label}
    sx={positive ? styles.stateBadgePositive : styles.stateBadgeNeutral}
  />
)

const IdentityProvider = ({ companyScopedSsoConfig, ssoSettingsSnapshot }) => {
  const { t } = UseTranslation()
  const certificatePem = ssoSettingsSnapshot.idp_certificate || ssoSettingsSnapshot.sp_certificate

  return (
    <CardWithSpace
      customStyles={styles.detailsCardShellIdpTop}
    >
      <Box sx={styles.detailsHeaderRow}>
        <Box sx={styles.detailsHeaderTitles}>
          <Typography component="h2" sx={styles.detailsTitle}>
            {t?.SsoIdentityProviderTitle}
          </Typography>
          <Typography sx={styles.detailsSubtitle}>
            {t?.SsoIdentityProviderSubtitle}
          </Typography>
        </Box>
        <Chip label={t?.SsoSaml2Badge} size="small" sx={styles.samlHeaderBadge} />
      </Box>
      <Box sx={styles.detailsBody}>
        <TitleWithInfo
          title={t?.Integration}
          info={<Chip label="SAML" size="small" sx={styles.integrationBadge} />}
          customStyle={styles.detailRow}
        />
        <TitleWithInfo
          title={t?.SsoDomainName}
          info={ssoSettingsSnapshot.domain}
          customStyle={styles.detailRow}
        />
        <TitleWithInfo
          title={t?.SsoEmployeeRelated}
          info={!companyScopedSsoConfig ? null : (
            <StateChip
              positive={Boolean(ssoSettingsSnapshot.employee_add_enabled)}
              label={ssoSettingsSnapshot.employee_add_enabled ? (t?.Enabled) : (t?.Disabled)}
            />
          )}
          customStyle={styles.detailRow}
        />
        <TitleWithInfo
          title={t?.SsoIdpMetadataUrl}
          info={ssoSettingsSnapshot.idp_metadata_url}
          customStyle={styles.detailRow}
        />
        <TitleWithInfo
          title={t?.SsoIdpEntityId}
          info={ssoSettingsSnapshot.entity_id}
          customStyle={styles.detailRow}
        />
        <TitleWithInfo
          title={t?.SsoIdpSsoUrl}
          info={ssoSettingsSnapshot.idp_sso_url}
          customStyle={styles.detailRow}
        />
        <TitleWithInfo
          title={t?.SsoIdpSloUrl}
          info={ssoSettingsSnapshot.idp_slo_url}
          customStyle={styles.detailRow}
        />
        <TitleWithInfo
          title={t?.SsoCertificate}
          info={(
            <Box sx={styles.certFieldColumn}>
              {certificatePem ? (
                <Typography
                  component="pre"
                  sx={[styles.detailValueMono, styles.certPemBlock]}
                >
                  {certificatePem}
                </Typography>
              ) : (
                <Typography sx={styles.noInfoText} variant="h6" >-- -- --</Typography>
              )}
            </Box>
          )}
          customStyle={styles.detailRow}
        />
      </Box>
    </CardWithSpace>
  )
}

const cardShellBase = {
  border: '1px solid #e8eaed',
  borderRadius: '12px',
  boxShadow: 'none',
  marginBottom: 0,
  overflow: 'hidden',
  padding: 0,
}

const styles = {
  detailsCardShellIdpTop: {
    ...cardShellBase,
    mt: 1.5,
  },
  detailsHeaderRow: {
    display: 'flex',
    flexDirection: { xs: 'row', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'flex-start' },
    justifyContent: 'flex-start',
    gap: { xs: 1.5, sm: 2 },
    px: { xs: 2.5, sm: 3 },
    pt: { xs: 2.5, sm: 3 },
    pb: { xs: 2, sm: 2.5 },
    borderBottom: '1px solid #ebecef',
  },
  detailsHeaderTitles: {
    flex: 1,
    minWidth: 0,
  },
  detailsTitle: {
    fontWeight: 700,
    fontSize: { xs: '1rem', sm: '1.0625rem' },
    color: '#1b2559',
    lineHeight: 1.35,
    letterSpacing: '-0.01em',
  },
  detailsSubtitle: {
    mt: 0.75,
    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
    fontWeight: 400,
    color: '#92929d',
    lineHeight: 1.45,
    maxWidth: '520px',
  },
  samlHeaderBadge: {
    height: 28,
    flexShrink: 0,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#29CC8B',
    backgroundColor: 'rgba(41, 204, 139, 0.12)',
    border: '1px solid #29CC8B',
    borderRadius: '999px',
    '& .MuiChip-label': { px: 1.25 },
  },
  detailsBody: {
    display: 'flex',
    flexDirection: 'column',
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    px: { xs: 2.5, sm: 3 },
    py: { xs: 2, sm: 2.25 },
    borderBottom: '1px solid #ebecef',
    marginRight: '0px !important',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(29, 158, 117, 0.05)',
    },
    ' & h5': {
      flexShrink: 0,
      width: { xs: '100px', sm: '300px' },
      minWidth: { xs: '100px', sm: '300px' },
      maxWidth: { xs: '100px', sm: '300px' },
      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
      fontWeight: 400,
      color: '#92929d',
      lineHeight: 1.4,
    },
    ' & h6': {
      textAlign: { xs: 'right', sm: 'left' },
      width: { xs: '100px', sm: 'unset' },
      fontSize: '14px',
      flex: 1,
      textTransform: 'none',
      wordBreak: 'break-word',
    },
  },
  detailValueMono: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
    fontWeight: 400,
    color: '#92929d',
    lineHeight: 1.45,
    wordBreak: 'break-all',
  },
  noInfoText: {
    fontSize: '18px',
    textTransform: 'capitalize',
    textAlign: 'left',
  },
  integrationBadge: {
    height: 28,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#4a4a5a',
    backgroundColor: '#f1f3f5',
    border: 'none',
    borderRadius: '999px',
    '& .MuiChip-label': {
      px: 1.5,
    },
  },
  certFieldColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 1,
    maxWidth: '100%',
  },
  certPemBlock: {
    m: 0,
    p: 1.5,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    bgcolor: '#f8f9fb',
    borderRadius: '8px',
    border: '1px solid #ebecef',
    fontSize: '0.7rem',
    lineHeight: 1.5,
    maxHeight: 220,
    overflow: 'auto',
  },
  statusDotIcon: (color) => ({
    '&&': {
      color: `${color} !important`,
      fontSize: 14,
    },
  }),
  stateBadgePositive: {
    height: 28,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#29CC8B',
    backgroundColor: 'rgba(41, 204, 139, 0.12)',
    border: '1px solid #29CC8B',
    borderRadius: '999px',
    '& .MuiChip-icon': { marginLeft: '8px' },
    '& .MuiChip-label': { px: 1 },
  },
  stateBadgeNeutral: {
    height: 28,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#696974',
    backgroundColor: 'rgba(146, 146, 157, 0.12)',
    border: '1px solid #d0d0d6',
    borderRadius: '999px',
    '& .MuiChip-icon': { marginLeft: '8px' },
    '& .MuiChip-label': { px: 1 },
  },
}

export default IdentityProvider
